/**
 * „Was ist neu“ — ein leiser Knopf mit Punkt; das Fenster öffnet sich nur auf Antippen.
 * (05.10.2026, Ticket 2931 — das Fenster selbst: 02.10.2026, Ticket 2925)
 *
 * Martin, 02.10.2026, 13:46: *„Die Neuigkeiten-Anzeige soll die Arbeit nicht stören oder verdecken. …
 * ein dezenter, immer sichtbarer Knopf (z. B. mit kleinem Punkt bei Neuem), den er antippt, wenn es ihm
 * passt. Kein Einblenden über dem Inhalt, kein Zwang, sofort zu reagieren.“*
 *
 * ## Was vorher war (gemessen am 05.10.2026, Stand pre.3635)
 *
 *   - Telefon (390 × 844): das Fenster ging von selbst auf und lag über dem Inhalt — die Karte 640 von 844
 *     Punkten hoch, davor ein Schleier über dem ganzen Bild.
 *   - Rechner und Tablet (820, 1440): es ging NIE auf. Die feste Seitenleiste trägt `aria-modal="true"`
 *     und setzt `body.drawer-open`; `fensterHindernis` meldete darum dauernd „anderes-fenster“.
 *   - Das Fenster zeigte nur, was NOCH NICHT geladen war (`seit` = laufende Version). Seit 25.09. lädt
 *     die App aber still nach, wenn sie verdeckt ist: im Journal vom 02.10. 11:00 bis 05.10. 10:33 stehen
 *     für Konto 1 36 stille Nachladungen auf 19 Versionen. Was dabei neu wurde, hat nie jemand gesehen.
 *
 * ## Was jetzt gilt
 *
 *   - Die App fragt nach den neuesten Sätzen überhaupt (`seit=0.0.0`, höchstens 12 Versionen mit allen ihren
 *     Sätzen, bis zur laufenden Version des Dienstes) und merkt SELBST, bis zu welcher Version sie angesehen sind. „Neu“ ist, was
 *     neuer ist als dieser Merker — gleich, ob es schon geladen ist oder erst nach dem Neuladen kommt.
 *   - Der Knopf ist da, sobald es Sätze gibt (nur Konto 1 — der Server gibt anderen eine leere Liste),
 *     und trägt einen Punkt, solange etwas ungesehen ist. Das Fenster öffnet NUR ein Tipp.
 *   - Wo der Knopf steht: an EINEM Ort in jeder Breite — in der Zeile „Version“ am Fuß des Menüs. Am
 *     Rechner ist das der Fuß der festen Seitenleiste (immer zu sehen), in der schmalen Symbolleiste dort
 *     nur das Zeichen mit Punkt, am Telefon die Zeile im Menü mit einem Punkt am Menü-Knopf der Fußleiste.
 *     ACHTUNG: Nicht in der Kopfleiste. Die erste Fassung (05.10. vormittags) stellte am Rechner ein Zeichen neben die
 *     Glocke — in genau die Leiste, die Martin am selben Tag als zu voll abgelehnt hat und die leerer
 *     wird. EIN Zustand, hier gesetzt (`verdrahteKnopf`); wie er in welcher Breite aussieht, zeichnet
 *     style.css.
 *   - Gemerkt wird beim Öffnen, nicht beim Schließen: wer mittendrin neu lädt, sieht den Punkt nicht wieder.
 *   - Die Liste gehört zu einer Version des Dienstes UND altert: der Server gibt nur her, was er selbst
 *     schon fährt. Lädt die App eine neue Fassung, bevor der Dienst neu gestartet ist (Journal 04.10.:
 *     still nachgeladen um 14:07, Dienst-Start erst 18:53), fehlte deren Satz — und nach dem Neustart
 *     fragte niemand mehr. Darum fragt der Takt nach `HALTBAR_MS` wieder, und ein Tipp will den Stand
 *     von jetzt (`TIPP_FRISCH_MS`), wartet darauf aber nur kurz (`TIPP_GEDULD_MS`).
 *
 * ## Die Teile
 *
 *   - `holeNeuigkeiten`   GET /api/neuigkeiten?seit=… — die „Für Martin:“-Sätze (server/was-ist-neu.ts).
 *   - `standAus`          Aus Liste und Merker: ist der Knopf da, wie viele Sätze sind ungesehen.
 *   - `verdrahteKnopf`    Der Knopf am Fuß des Menüs und der Punkt am Menü-Knopf der Fußleiste.
 *   - `zeigeWasIstNeu`    Das Fenster, gebaut aus den Bausteinen der Sheets (.sheet/.sheet-card).
 *   - `erzeugeWasIstNeu`  Der Besitzer des Ablaufs: wer fragt, wann, was gemerkt wird.
 *
 * ## Rückweg
 *
 * `WAS_IST_NEU_KNOPF=0` am Dienst (Antwort trägt `knopf: false`): kein Knopf, das Fenster öffnet sich
 * wieder von selbst, wenn die Pille „Neue Version“ da ist, etwas ungesehen ist und nichts stört
 * (`fensterHindernis`). Jedes Öffnen schreibt eine Journalzeile `[was-ist-neu] geöffnet weg=…`.
 */

import { rueckfallKurz } from "../../shared/neu-kurz-rueckfall.js";
import { istNeuerAls, istWebVersion } from "../../shared/web-version.js";
import { zeichen } from "./zeichen.js";
import { bauePunkt, punktAus, punkteVon, type NeuPunktAnzeige } from "./neu-punkt.js";
import type { KartenOptionen } from "./neu-chip.js";

/** So viele Neuerungen zeigt die Zahl höchstens; darüber „99+“. */
export const NEU_ZAHL_MAX = 99;

/** Wo die zuletzt angesehene Version steht. */
export const GESEHEN_SCHLUESSEL = "voice-v8.was-ist-neu.gesehen";

/** Nach einem Fehler frühestens nach so vielen Millisekunden erneut fragen. */
export const NACH_FEHLER_MS = 60_000;

/**
 * So lange gilt eine Liste mit Sätzen als frisch; danach fragt der nächste Takt wieder — nur bei sichtbarer
 * Seite. Fünf Minuten wie der Prüf-Takt der App (checkForUpdate): nach einem Neustart des Dienstes steht der
 * Punkt spätestens dann da, und beim Zurückkommen aus dem Hintergrund sofort.
 */
export const HALTBAR_MS = 5 * 60_000;

/** Ein Tipp will den Stand von jetzt: ist die Liste älter, wird vor dem Zeigen gefragt (gedrosselt auf diese Frist). */
export const TIPP_FRISCH_MS = 30_000;

/** So lange wartet ein Tipp auf die frische Liste, bevor er die vorhandene zeigt — ein Tipp ohne Wirkung wäre schlimmer als ein alter Stand. */
export const TIPP_GEDULD_MS = 1_500;

/** Der Konto-Stand wird bei sichtbarer Seite höchstens alle so viele Millisekunden neu geholt (Takt der App) — das andere Gerät zieht binnen Sekunden nach. */
export const KONTO_TAKT_MS = 30_000;

/** Beim Zurückkommen in die App (visibilitychange/focus) wird sofort geholt; doppelte Auslöser (beide feuern) fasst diese Frist zusammen. */
export const KONTO_ZUSAMMEN_MS = 2_000;

/** Länger hängt keine Frage (Funkloch): danach gilt sie als gescheitert und macht der nächsten Platz. */
export const HOL_FRIST_MS = 15_000;

/** `seit` für „die neuesten Sätze überhaupt“ — jede Version der App ist neuer als diese. */
export const VON_ANFANG = "0.0.0";

export interface NeuEintrag {
  version: string;
  datum: string;
  text: string;
  /** Die Zeile in einzelne Neuerungen zerlegt (Server, shared/neu-punkte.ts); fehlt bei älteren Diensten. */
  punkte?: NeuPunktAnzeige[];
}

export interface NeuigkeitenListe {
  eintraege: NeuEintrag[];
  mehr: boolean;
  /** `false` nur, wenn der Dienst den Knopf ausgeschaltet hat (WAS_IST_NEU_KNOPF=0). */
  knopf: boolean;
  /** Der Dienst bietet die Kurzfassung an (NEUIGKEITEN_KURZ nicht 0). Fehlt das Feld (alter Dienst), zeigt die Karte wie früher alles. */
  kurz?: boolean;
}

/** „8.0.0-pre.3588“ → „pre.3588“ (so kennt Martin die Nummer). */
export function kurzVersion(v: string): string {
  return v.replace(/^\d+\.\d+\.\d+-/, "");
}

/** Die Liste vom Server — null bei jedem Fehler (dann: nichts ändern, später wieder). */
export async function holeNeuigkeiten(
  seit: string,
  holen: typeof fetch = (...a) => fetch(...a),
): Promise<NeuigkeitenListe | null> {
  try {
    const r = await holen(`/api/neuigkeiten?seit=${encodeURIComponent(seit)}`, {
      cache: "no-store",
      credentials: "same-origin",
      // Ohne Frist bliebe eine hängende Frage die einzige (es läuft immer nur EINE) — und der Stand fröre ein.
      ...(typeof AbortSignal !== "undefined" && typeof AbortSignal.timeout === "function"
        ? { signal: AbortSignal.timeout(HOL_FRIST_MS) }
        : {}),
    });
    if (!r.ok) return null;
    const j = (await r.json()) as { ok?: boolean; eintraege?: unknown; mehr?: unknown; knopf?: unknown; kurz?: unknown };
    if (j.ok === false || !Array.isArray(j.eintraege)) return null;
    const eintraege = j.eintraege.flatMap((e): NeuEintrag[] => {
      const o = e as (Partial<Omit<NeuEintrag, "punkte">> & { punkte?: unknown }) | null;
      if (!o || typeof o.text !== "string" || !o.text.trim()) return [];
      const punkte = Array.isArray(o.punkte) ? o.punkte.flatMap((p) => punktAus(p) ?? []) : [];
      return [
        {
          version: String(o.version ?? ""),
          datum: String(o.datum ?? ""),
          text: o.text.trim(),
          ...(punkte.length > 0 ? { punkte } : {}),
        },
      ];
    });
    // Ein Dienst, der das Feld noch nicht kennt, meint den Knopf.
    return { eintraege, mehr: j.mehr === true, knopf: j.knopf !== false, ...(j.kurz === true ? { kurz: true } : {}) };
  } catch {
    return null;
  }
}

/** So lange wartet die Anfrage nach der KI-Kurzfassung höchstens (der Server gibt der KI 6 s); die Karte steht schon, der Rückfall bleibt. */
export const KURZ_WARTE_MS = 7_000;

/** Höchstens so viele Punkte und Zeichen nimmt die Karte von der Kurzfassung an (Server: 5 × 110). */
const KURZ_PUNKTE_MAX = 5;
const KURZ_ZEICHEN_MAX = 110;

/**
 * Die Kurzfassung der Neuigkeiten neuer als `seit` — null bei jedem Fehler oder allem, was nicht genau passt (dann zeigt die
 * Karte wie früher alles). Streng gelesen: eine Liste aus 1…5 nicht leeren Texten, je höchstens 110 Zeichen.
 */
export async function holeKurzfassung(
  seit: string,
  holen: typeof fetch = (...a) => fetch(...a),
  frist: number = KURZ_WARTE_MS,
): Promise<string[] | null> {
  try {
    const r = await holen(`/api/neuigkeiten/kurz?seit=${encodeURIComponent(seit)}`, {
      cache: "no-store",
      credentials: "same-origin",
      ...(typeof AbortSignal !== "undefined" && typeof AbortSignal.timeout === "function" ? { signal: AbortSignal.timeout(frist) } : {}),
    });
    if (!r.ok) return null;
    const j = (await r.json()) as { ok?: boolean; kurz?: unknown };
    if (j.ok === false || !Array.isArray(j.kurz) || j.kurz.length === 0 || j.kurz.length > KURZ_PUNKTE_MAX) return null;
    const punkte = j.kurz.map((p) => (typeof p === "string" ? p.trim() : ""));
    return punkte.every((p) => p !== "" && p.length <= KURZ_ZEICHEN_MAX) ? punkte : null;
  } catch {
    return null;
  }
}

// --- Was ist ungesehen ----------------------------------------------------

/**
 * Hat der Leser diese Version noch nicht angesehen?
 *
 * Ohne Merker (neues Gerät, gelöschte Website-Daten) ist alles ungesehen — der Punkt lädt dann einmal
 * ein, die neuesten Sätze anzusehen. Ein UNLESBARER Merker zählt wie keiner: `istNeuerAls` meldet bei
 * Unlesbarem „nein“, und mit ihm als Merker wäre nie wieder etwas neu — er würde beim Öffnen auch nicht
 * überschrieben (gemessen am 05.10.2026: Stand neu=0, nach dem Öffnen unverändert). So heilt er sich:
 * einmal ein Punkt, ein Tipp, und es steht wieder eine lesbare Version da.
 */
export function istUngesehen(version: string, gesehen: string): boolean {
  return istWebVersion(gesehen) ? istNeuerAls(version, gesehen) : true;
}

/** Was die Knöpfe zeigen. */
export interface Stand {
  /** Es gibt Sätze und der Knopf ist an — der Knopf steht da. */
  da: boolean;
  /** So viele der gelieferten Neuerungen sind ungesehen (je Eintrag seine Punkte, ohne Zerlegung einer). */
  neu: number;
  /** Alle gelieferten sind ungesehen und der Server hat noch mehr: es sind mehr als `neu`. */
  mehrNeu: boolean;
}

const NICHTS: Stand = { da: false, neu: 0, mehrNeu: false };

export function standAus(liste: NeuigkeitenListe | null, gesehen: string): Stand {
  if (!liste || liste.eintraege.length === 0) return NICHTS;
  const ungesehen = liste.eintraege.filter((e) => istUngesehen(e.version, gesehen));
  const roh = ungesehen.reduce((n, e) => n + punkteVon(e).length, 0);
  // Ein Gerät ohne Merker zählt nie „in die Hunderte“: über der Obergrenze steht „99+“ (nur ein Dienst ohne Deckel käme dahin).
  const zuViel = roh > NEU_ZAHL_MAX;
  return {
    da: liste.knopf,
    neu: zuViel ? NEU_ZAHL_MAX : roh,
    mehrNeu: zuViel || (liste.mehr && ungesehen.length === liste.eintraege.length),
  };
}

/** Der Name des Knopfs für Vorleser und als Hinweis unter der Maus — der Punkt allein sagt nichts. */
export function knopfSatz(s: Stand): string {
  if (s.neu === 0) return "Was ist neu";
  if (s.mehrNeu) return `Was ist neu – mehr als ${s.neu} Neuigkeiten, noch nicht angesehen`;
  return `Was ist neu – ${s.neu === 1 ? "1 Neuigkeit" : `${s.neu} Neuigkeiten`}, noch nicht angesehen`;
}

// --- Rückweg: wann ein Fenster von selbst stören würde ---------------------

/** Was gerade läuft — alles, was ein Fenster stören würde. */
export interface FensterZustand {
  /** Anruf aktiv. */
  anruf: boolean;
  /** Sprechtaste hält, nimmt auf oder sendet. */
  sprechtaste: boolean;
  /** Eine Aufnahme läuft (Mikrofon offen, Tagesaufnahme). */
  aufnahme: boolean;
  /** Es klingt etwas (Vorlesen, Spur, Musik). */
  ton: boolean;
  /** Die Seite ist sichtbar. */
  sichtbar: boolean;
}

/**
 * Warum das Fenster jetzt NICHT von selbst aufgeht — `null` heißt: darf. Nur für den Rückweg
 * (WAS_IST_NEU_KNOPF=0); ein Tipp auf den Knopf fragt nicht.
 */
export function fensterHindernis(z: FensterZustand, doc: Document = document): string | null {
  if (!z.sichtbar) return "verdeckt";
  if (z.anruf) return "anruf";
  if (z.sprechtaste) return "sprechtaste";
  if (z.aufnahme) return "aufnahme";
  if (z.ton) return "ton";
  /*
   * Jedes andere Fenster der App trägt aria-modal; das Menü steht als drawer-open.
   * ⚠️ Die feste Seitenleiste am Rechner ist KEIN Fenster, trägt aber beides dauernd (`.nav-drawer.is-desktop`,
   *    app.ts openDrawer). Gemessen am 05.10.2026 bei 820 und 1440 Punkten: mit ihr als Hindernis ging das
   *    Fenster am Rechner nie auf.
   */
  for (const f of doc.querySelectorAll('[aria-modal="true"]:not([hidden])')) {
    if (!f.classList.contains("is-desktop")) return "anderes-fenster";
  }
  if (doc.body?.classList.contains("drawer-open") && !doc.querySelector(".nav-drawer.is-desktop")) return "menue";
  return null;
}

/**
 * Der Gelesen-Stand im KONTO (Server, GET/PUT /api/neuigkeiten/gesehen): alle Geräte sehen dieselbe Zahl.
 * Beide Wege werfen nie: ein Fehler (offline, 4xx, 5xx) heißt „kein Serverstand“ bzw. „nicht gespeichert“ — der
 * lokale Merker trägt dann allein, wie vor dem Konto-Stand.
 */
export interface KontoMerker {
  /** Die Version, bis zu der das Konto gelesen hat; "" wenn keine oder nicht erreichbar. */
  hole(): Promise<string>;
  /** Das Konto bis zu dieser Version lesen lassen (der Server geht nie rückwärts). */
  setze(version: string): Promise<void>;
}

export function serverKontoMerker(holen: typeof fetch = (...a) => fetch(...a)): KontoMerker {
  const frist = (): { signal?: AbortSignal } =>
    typeof AbortSignal !== "undefined" && typeof AbortSignal.timeout === "function"
      ? { signal: AbortSignal.timeout(HOL_FRIST_MS) }
      : {};
  return {
    async hole() {
      try {
        const r = await holen("/api/neuigkeiten/gesehen", { cache: "no-store", credentials: "same-origin", ...frist() });
        if (!r.ok) return "";
        const j = (await r.json()) as { gesehen?: unknown };
        return typeof j.gesehen === "string" && istWebVersion(j.gesehen) ? j.gesehen : "";
      } catch {
        return "";
      }
    },
    async setze(version) {
      try {
        await holen("/api/neuigkeiten/gesehen", {
          method: "PUT",
          cache: "no-store",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ version }),
          ...frist(),
        });
      } catch {
        /* offline — der lokale Merker hat es; beim nächsten Abgleich zieht der Ablauf es nach */
      }
    },
  };
}

/** Der Merkspeicher — nie werfen: ohne localStorage bleibt nur der Arbeitsspeicher. */
export interface Merkspeicher {
  lies(): string;
  schreibe(version: string): void;
}

export function localStorageMerker(): Merkspeicher {
  return {
    lies() {
      try {
        return localStorage.getItem(GESEHEN_SCHLUESSEL) ?? "";
      } catch {
        return "";
      }
    },
    schreibe(version) {
      try {
        localStorage.setItem(GESEHEN_SCHLUESSEL, version);
      } catch {
        /* Speicher gesperrt oder voll — der Arbeitsspeicher genügt für diese Sitzung */
      }
    },
  };
}

// --- Das Fenster ----------------------------------------------------------

/** Ein Satz im Fenster, mit dem, was der Ablauf über ihn weiß. */
interface FensterEintrag extends NeuEintrag {
  /** Noch nicht angesehen — steht oben, über „Schon gesehen“. */
  neu?: boolean;
  /** Neuer als die laufende App: gilt erst nach dem Neuladen. */
  nachNeuladen?: boolean;
}

export interface FensterOptionen {
  eintraege: FensterEintrag[];
  /** Es gibt mehr Ungesehenes, als die Liste trägt. */
  mehr: boolean;
  /** Eine neue Version liegt bereit: „Später“ und „Jetzt neu laden“ statt nur „Schließen“. */
  updateBereit?: boolean;
  /** „Jetzt neu laden“ — der Aufrufer nimmt denselben Weg wie die Pille. */
  onNeuLaden: () => void;
  /** „Alles gelesen“ getippt: der Aufrufer setzt den Konto-Stand auf die jüngste Version und zeichnet den Chip neu. */
  onGelesen?: () => void;
  /** Das Fenster ist zu (egal wie), einmal. */
  onZu?: () => void;
  /** Wohin der Fokus beim Schließen geht, in dieser Reihenfolge; danach dorthin, wo er vorher war. */
  fokusZurueck?: Array<HTMLElement | null | undefined>;
}

export interface Fenster {
  el: HTMLElement;
  schliesse: () => void;
}

const FOKUSSIERBAR =
  'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Ab so vielen Punkten Wischweg nach unten gilt es als Schließen. */
const WISCH_SCHWELLE = 80;

function neuerAbschnitt(e: FensterEintrag): HTMLLIElement {
  const li = document.createElement("li");
  li.className = "wasneu-eintrag";
  const meta = document.createElement("div");
  meta.className = "wasneu-meta";
  meta.textContent = [kurzVersion(e.version), e.datum, e.nachNeuladen ? "erst nach dem Neuladen" : ""]
    .filter(Boolean)
    .join(" · ");
  li.appendChild(meta);
  // Je Neuerung ein Eintrag mit Überschrift, Ort und Bild (lib/neu-punkt.ts) — als textContent, nie innerHTML.
  for (const p of punkteVon(e)) li.appendChild(bauePunkt(p, "div", "wasneu-text"));
  return li;
}

function neueGruppe(eintraege: FensterEintrag[], name: { label?: string; labelledby?: string }): HTMLOListElement {
  const ol = document.createElement("ol");
  ol.className = "wasneu-gruppe";
  if (name.label) ol.setAttribute("aria-label", name.label);
  if (name.labelledby) ol.setAttribute("aria-labelledby", name.labelledby);
  for (const e of eintraege) ol.appendChild(neuerAbschnitt(e));
  return ol;
}

/** Steht das Element da, wo ein Fokus ankommen kann? (Der Knopf im Menü ist zu, sobald das Menü zu ist.) */
function erreichbar(el: HTMLElement | null | undefined): el is HTMLElement {
  return !!el && el.isConnected && !el.closest("[hidden]");
}

export function zeigeWasIstNeu(o: FensterOptionen): Fenster {
  const vorher = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  const el = document.createElement("div");
  el.className = "sheet sheet--modal sheet--menu wasneu";
  el.setAttribute("role", "dialog");
  el.setAttribute("aria-modal", "true");
  el.setAttribute("aria-labelledby", "wasneu-titel");

  const backdrop = document.createElement("div");
  backdrop.className = "sheet-backdrop";
  el.appendChild(backdrop);

  const card = document.createElement("div");
  card.className = "sheet-card wasneu-karte";
  // Der Fokus beginnt auf der Karte, nicht auf einem Knopf: ein Enter, das gerade
  // einen Satz abschicken sollte, darf nicht die Seite neu laden.
  card.tabIndex = -1;

  const kopf = document.createElement("div");
  kopf.className = "wasneu-kopf";
  const marke = document.createElement("span");
  marke.className = "wasneu-zeichen";
  marke.setAttribute("aria-hidden", "true");
  // Dieselbe Zeichnung wie am Knopf, aus dem Vorrat (kein Text eines Satzes — die stehen als textContent da).
  marke.innerHTML = zeichen("neu", 20);
  const titel = document.createElement("h2");
  titel.className = "sheet-title wasneu-titel";
  titel.id = "wasneu-titel";
  titel.textContent = "Was ist neu";
  kopf.append(marke, titel);
  card.appendChild(kopf);

  /*
   * Oben, was der Leser noch nicht angesehen hat; darunter — durch EINE Zeile getrennt, nicht durch ein
   * Abzeichen an jedem Satz — was er schon kennt. Ist nichts neu, sagt es ein Satz.
   */
  const liste = document.createElement("div");
  liste.className = "wasneu-liste";
  const neue = o.eintraege.filter((e) => e.neu);
  const alte = o.eintraege.filter((e) => !e.neu);
  if (neue.length > 0) {
    liste.appendChild(neueGruppe(neue, { label: "Neu, noch nicht angesehen" }));
  } else {
    const ruhig = document.createElement("p");
    ruhig.className = "wasneu-ruhig";
    ruhig.textContent = "Seit deinem letzten Blick ist nichts dazugekommen.";
    liste.appendChild(ruhig);
  }
  if (alte.length > 0) {
    if (neue.length > 0) {
      const trenner = document.createElement("h3");
      trenner.className = "wasneu-trenner";
      trenner.id = "wasneu-gesehen";
      trenner.textContent = "Schon gesehen";
      liste.append(trenner, neueGruppe(alte, { labelledby: "wasneu-gesehen" }));
    } else {
      liste.appendChild(neueGruppe(alte, { label: "Zuletzt" }));
    }
  }
  card.appendChild(liste);

  if (o.mehr) {
    const mehr = document.createElement("p");
    mehr.className = "wasneu-mehr";
    mehr.textContent = "… und weitere kleine Verbesserungen";
    card.appendChild(mehr);
  }

  const reihe = document.createElement("div");
  reihe.className = "wasneu-aktionen";
  let gelesen: HTMLButtonElement | null = null;
  if (neue.length > 0 && o.onGelesen) {
    gelesen = document.createElement("button");
    gelesen.type = "button";
    gelesen.className = "btn btn--primary wasneu-gelesen";
    gelesen.textContent = "Alles gelesen";
    gelesen.setAttribute("aria-label", "Alles als gelesen markieren");
    reihe.appendChild(gelesen);
  }
  const spaeter = document.createElement("button");
  spaeter.type = "button";
  spaeter.className = "btn btn--ghost wasneu-spaeter";
  // Ohne wartende Version gibt es nichts aufzuschieben — dann heißt der Knopf, was er tut.
  spaeter.textContent = o.updateBereit ? "Später" : "Schließen";
  reihe.appendChild(spaeter);
  let neu: HTMLButtonElement | null = null;
  if (o.updateBereit) {
    neu = document.createElement("button");
    neu.type = "button";
    neu.className = "btn btn--primary wasneu-neu";
    neu.textContent = "Jetzt neu laden";
    reihe.appendChild(neu);
  }
  card.appendChild(reihe);
  el.appendChild(card);

  let zu = false;
  const schliesse = (): void => {
    if (zu) return;
    zu = true;
    document.removeEventListener("keydown", beiTaste, true);
    el.remove();
    // Der Fokus geht zum Knopf zurück, der geöffnet hat — sonst dorthin, wo er war, wenn es das noch gibt.
    const ziel = [...(o.fokusZurueck ?? []), vorher].find(erreichbar);
    if (ziel) {
      try {
        ziel.focus({ preventScroll: true });
      } catch {
        /* nicht fokussierbar — dann bleibt er, wo er ist */
      }
    }
    o.onZu?.();
  };

  function beiTaste(ev: KeyboardEvent): void {
    if (ev.key === "Escape") {
      ev.preventDefault();
      ev.stopPropagation();
      schliesse();
      return;
    }
    if (ev.key !== "Tab") return;
    // Fokusfalle: Tab und Umschalt-Tab bleiben im Fenster.
    const felder = [...card.querySelectorAll<HTMLElement>(FOKUSSIERBAR)];
    const erstes = felder[0];
    const letztes = felder[felder.length - 1];
    if (!erstes || !letztes) return;
    const aktiv = document.activeElement;
    if (!el.contains(aktiv) || aktiv === card) {
      ev.preventDefault();
      (ev.shiftKey ? letztes : erstes).focus();
    } else if (ev.shiftKey && aktiv === erstes) {
      ev.preventDefault();
      letztes.focus();
    } else if (!ev.shiftKey && aktiv === letztes) {
      ev.preventDefault();
      erstes.focus();
    }
  }
  document.addEventListener("keydown", beiTaste, true);

  backdrop.addEventListener("click", schliesse);
  spaeter.addEventListener("click", schliesse);
  gelesen?.addEventListener("click", () => {
    o.onGelesen?.();
    gelesen?.remove();
    // Der Fokus darf nicht mit dem Knopf verschwinden.
    card.focus({ preventScroll: true });
  });
  neu?.addEventListener("click", () => {
    schliesse();
    o.onNeuLaden();
  });

  // Wischen nach unten schließt — nur vom oberen Rand der Liste aus, damit das
  // Lesen (Scrollen) nie ein Schließen wird. Rein passiv: kein preventDefault.
  let startY: number | null = null;
  card.addEventListener(
    "touchstart",
    (ev) => {
      startY = ev.touches.length === 1 && liste.scrollTop <= 0 ? (ev.touches[0]?.clientY ?? null) : null;
    },
    { passive: true },
  );
  card.addEventListener(
    "touchend",
    (ev) => {
      const y = ev.changedTouches[0]?.clientY;
      if (startY !== null && y !== undefined && y - startY >= WISCH_SCHWELLE) schliesse();
      startY = null;
    },
    { passive: true },
  );

  document.body.appendChild(el);
  card.focus({ preventScroll: true });
  return { el, schliesse };
}

// --- Der Knopf ------------------------------------------------------------

/** Kennungen im Markup (index.html). `hidden` und `data-neu` am Knopf gehören allein diesem Modul. */
export const KNOPF = "nav-neu";
/** Der Satz für Vorleser am Menü-Knopf der Fußleiste (aria-describedby dort). */
export const MENUE_HINWEIS = "wasneu-menue-hinweis";
const MENUE_HINWEIS_SATZ = "Neues unter „Was ist neu“";

/** Was der Ablauf zeichnen lässt: den Knopf und den Punkt am Menü-Knopf der Fußleiste. */
export interface Anzeige {
  /** Den Stand an den Knopf und an den Menü-Knopf der Fußleiste schreiben. */
  zeichne: (s: Stand) => void;
  /** Steht das Fenster? (aria-expanded am Knopf) */
  offen: (an: boolean) => void;
}

/**
 * Den Knopf verdrahten: Zeichnung einsetzen, Tipp weitergeben, Stand zeichnen.
 *
 * ## EIN Knopf, EIN Ort
 *
 * Der Knopf steht in der Zeile „Version“ am Fuß des Menüs — am Telefon in der Schublade, am Rechner im
 * Fuß der festen Seitenleiste, in der schmalen Symbolleiste als Zeichen allein (style.css,
 * `body.nav-rail`). Dieses Modul setzt nur den Zustand: `hidden` (es gibt Sätze), `data-neu` (etwas ist
 * ungesehen) und dasselbe als EIN Merkmal am body (`data-wasneu-menue`) für den Punkt am Menü-Knopf der
 * Fußleiste — den Weg zum Knopf, solange das Menü zu ist. Ob dieser Punkt gerade gebraucht wird (Menü
 * zu) oder ein zweiter wäre (Leiste steht), entscheidet style.css an `body.drawer-open`; hier wird
 * nichts gemessen und nichts nachgerechnet.
 */
export function verdrahteKnopf(onTippen: (von: HTMLElement) => void, doc: Document = document): Anzeige {
  const knopf = doc.getElementById(KNOPF);
  const hinweis = doc.getElementById(MENUE_HINWEIS);

  if (knopf) {
    const platz = knopf.querySelector<HTMLElement>("[data-wasneu-zeichen]");
    if (platz) platz.innerHTML = zeichen("neu", Number(platz.dataset.wasneuZeichen) || 20);
    knopf.addEventListener("click", () => onTippen(knopf));
  }

  /** Nur schreiben, was sich ändert: der Takt zeichnet alle acht Sekunden denselben Stand. */
  const zeichne = (s: Stand): void => {
    const punkt = s.da && s.neu > 0;
    const satz = knopfSatz(s);
    if (knopf) {
      if (knopf.hidden === s.da) knopf.hidden = !s.da;
      if (knopf.hasAttribute("data-neu") !== punkt) knopf.toggleAttribute("data-neu", punkt);
      if (knopf.getAttribute("aria-label") !== satz) knopf.setAttribute("aria-label", satz);
      // In der Symbolleiste steht nur das Zeichen da — der Hinweis unter der Maus nennt es.
      if (knopf.title !== satz) knopf.title = satz;
    }
    const body = doc.body;
    if (punkt && body.dataset.wasneuMenue !== "neu") body.dataset.wasneuMenue = "neu";
    else if (!punkt && body.dataset.wasneuMenue !== undefined) delete body.dataset.wasneuMenue;
    const text = punkt ? MENUE_HINWEIS_SATZ : "";
    if (hinweis && hinweis.textContent !== text) hinweis.textContent = text;
  };

  return {
    zeichne,
    offen: (an) => knopf?.setAttribute("aria-expanded", an ? "true" : "false"),
  };
}

/**
 * In welcher Gestalt der Knopf dastand, als er getippt wurde — fürs Journal: wird der Weg gefunden, und wo?
 * `menue` = Schublade am Telefon, `leiste` = feste Seitenleiste am Rechner, `rail` = die Symbolleiste.
 */
function ortVon(von: HTMLElement | null): "-" | "menue" | "leiste" | "rail" {
  if (!von) return "-";
  if (!von.closest(".nav-drawer.is-desktop")) return "menue";
  return von.ownerDocument.body.classList.contains("nav-rail") ? "rail" : "leiste";
}

// --- Der Ablauf -----------------------------------------------------------

interface WasIstNeuAbhaengigkeiten {
  /** Meldet die App gerade eine neuere Version (Pille sichtbar)? */
  updateBereit: () => boolean;
  /** Die Version, die der Server meldet (build-version.json), z. B. „8.0.0-pre.3590“ — leer, wenn keine neuere. */
  serverVersion: () => string;
  /** Die Version, die gerade in dieser App läuft. */
  laufendeVersion: () => string;
  /** Was gerade läuft — für den Rückweg (Fenster von selbst), und `sichtbar` für die Nachfrage im Takt. */
  zustand: () => FensterZustand;
  /** „Jetzt neu laden“ — derselbe Weg wie die Pille. */
  neuLaden: () => void;
  /** Der Knopf und der Punkt am Menü-Knopf (Tests setzen eine Attrappe; ohne sie zeichnet niemand). */
  anzeige?: Anzeige;
  /** Das Menü am Telefon schließen, bevor das Fenster aufgeht (es läge sonst darüber). */
  menueZu?: () => void;
  /** Wohin der Fokus geht, wenn der Knopf beim Schließen nicht mehr dasteht (Menü zu): der Menü-Knopf. */
  fokusErsatz?: () => HTMLElement | null;
  /** Eine Zeile fürs Journal (app.ts: meldeDiagnose). */
  melde?: (zeile: string) => void;
  holen?: typeof fetch;
  merker?: Merkspeicher;
  /** Der Gelesen-Stand im Konto (app.ts: serverKontoMerker); ohne ihn gilt nur der lokale Merker. */
  konto?: KontoMerker;
  /** Die Karte „Das ist neu“ in den Chat legen (Neu-Chip, lib/neu-chip.ts; app.ts setzt sie ein). */
  karte?: (o: KartenOptionen) => void;
  /** Das Fenster bauen (Tests setzen eine Attrappe). */
  zeige?: (o: FensterOptionen) => Fenster;
  jetzt?: () => number;
  /** Eine Frist abwarten (nur der Tipp: wie lange er auf die frische Liste wartet). */
  warte?: (ms: number) => Promise<void>;
  doc?: Document;
}

interface WasIstNeu {
  /**
   * Der Anzeige-Takt (und der Start): die Liste holen, wenn sie fehlt, eine andere Version da ist oder sie
   * alt geworden ist, und den Stand zeichnen. Öffnet NICHTS — außer im Rückweg (Knopf aus). Wirft nie, ist billig.
   */
  versuche: () => Promise<void>;
  /** Ein Tipp auf den Knopf: das Fenster zeigen. `von` bekommt den Fokus beim Schließen zurück. */
  oeffne: (von?: HTMLElement | null) => Promise<void>;
  /**
   * Den Gelesen-Stand des Kontos neu holen (App-Takt alle `KONTO_TAKT_MS` bei sichtbarer Seite; `sofort` beim
   * Zurückkommen in die App): hat ein anderes Gerät gelesen, zeichnet sich die Zahl hier neu. Wirft nie.
   */
  gleiche: (sofort?: boolean) => Promise<void>;
  /** Ein Tipp auf den Neu-Chip: die Karte in den Chat legen. Merkt NICHTS — erst das Ansehen der Karte (`SICHT_MS`) setzt den Merker. */
  zeigeKarte: () => Promise<void>;
  /** Steht das Fenster gerade? */
  istOffen: () => boolean;
  /** Der Stand, wie ihn der Knopf zuletzt bekommen hat. */
  stand: () => Stand;
}

export function erzeugeWasIstNeu(d: WasIstNeuAbhaengigkeiten): WasIstNeu {
  const merker = d.merker ?? localStorageMerker();
  const jetzt = d.jetzt ?? (() => Date.now());
  const zeige = d.zeige ?? zeigeWasIstNeu;
  const warte = d.warte ?? ((ms: number) => new Promise<void>((ok) => setTimeout(ok, ms)));
  /** Arbeitsspeicher-Merkung: gilt auch, wenn localStorage ausfällt (dann je Sitzung). */
  let gesehenSitzung = "";
  /** Was das Konto zuletzt gemeldet hat (""= unbekannt) — das Neuere von Konto und Gerät gilt. */
  let gesehenKonto = "";
  /** Die Liste gehört zu einer Version des Dienstes und hat ein Alter: beides entscheidet, ob neu gefragt wird. */
  let zwischen: { schluessel: string; liste: NeuigkeitenListe; geholt: number } | null = null;
  let frage: Promise<NeuigkeitenListe | null> | null = null;
  let fruehestens = 0;
  let offen = false;
  let letzter: Stand = NICHTS;

  /** Der neuere von Speicher und Sitzung — der Merker läuft nie rückwärts. Ein unlesbarer zählt wie keiner. */
  const gesehen = (): string => {
    const roh = merker.lies();
    const fest = istWebVersion(roh) ? roh : "";
    const lokal = gesehenSitzung && (!fest || istNeuerAls(gesehenSitzung, fest)) ? gesehenSitzung : fest;
    return gesehenKonto && (!lokal || istNeuerAls(gesehenKonto, lokal)) ? gesehenKonto : lokal;
  };

  /**
   * Konto und Gerät abgleichen (bei jeder frischen Liste): ist das Konto weiter, zieht das Gerät nach (auch in den
   * lokalen Merker); ist das Gerät weiter, zieht das Konto nach. Wirft nie, blockiert nichts.
   */
  async function gleicheAb(): Promise<void> {
    if (!d.konto) return;
    try {
      const konto = await d.konto.hole();
      if (konto && (!gesehenKonto || istNeuerAls(konto, gesehenKonto))) gesehenKonto = konto;
      const lokal = gesehen();
      const festLokal = merker.lies();
      if (konto && (!istWebVersion(festLokal) || istNeuerAls(konto, festLokal))) merker.schreibe(konto);
      if (lokal && (!konto || istNeuerAls(lokal, konto))) void d.konto.setze(lokal);
    } catch (err) {
      warne(err);
    }
  }

  let abgleichLaeuft: Promise<void> | null = null;
  let letzterAbgleich: number | null = null;

  /**
   * Der Abgleich mit dem Konto, eine Frage zugleich. Ist die letzte Frage jünger als `mindestensMs`, wird nicht
   * neu gefragt. Zeichnet die Zahl neu, sobald der Stand da ist. Wirft nie.
   */
  function abgleichen(mindestensMs: number): Promise<void> {
    if (!d.konto) return Promise.resolve();
    if (abgleichLaeuft) return abgleichLaeuft;
    if (letzterAbgleich !== null && jetzt() - letzterAbgleich < mindestensMs) return Promise.resolve();
    letzterAbgleich = jetzt();
    abgleichLaeuft = gleicheAb().then(() => {
      abgleichLaeuft = null;
      zeichne();
    });
    return abgleichLaeuft;
  }

  const zeichne = (): Stand => {
    letzter = standAus(zwischen?.liste ?? null, gesehen());
    d.anzeige?.zeichne(letzter);
    return letzter;
  };

  /**
   * Die Liste zur Version des Dienstes — aus dem Zwischenspeicher, solange sie höchstens `hoechstAlterMs`
   * alt ist, sonst EINE Frage; nach einem Fehler der alte Stand.
   *
   * Eine LEERE Liste altert nicht: sie heißt „anderes Konto“ (der Server gibt nur Konto 1 Sätze), und dort
   * soll der Takt nicht alle fünf Minuten nach etwas fragen, das nie kommt. Eine neue Version fragt wieder.
   */
  function liste(hoechstAlterMs: number): Promise<NeuigkeitenListe | null> {
    const schluessel = d.serverVersion() || d.laufendeVersion();
    if (!schluessel) return Promise.resolve(null);
    const z = zwischen;
    if (z?.schluessel === schluessel && (z.liste.eintraege.length === 0 || jetzt() - z.geholt < hoechstAlterMs)) {
      return Promise.resolve(z.liste);
    }
    if (jetzt() < fruehestens) return Promise.resolve(z?.liste ?? null);
    // Der Abgleich hängt NICHT an der Liste: ein hängender Konto-GET (bis HOL_FRIST_MS) verzögert sie nicht; die Zahl
    // zeichnet sich neu, sobald der Stand da ist.
    if (!frage) void abgleichen(0);
    frage ??= holeNeuigkeiten(VON_ANFANG, d.holen).then((l) => {
      frage = null;
      if (l) zwischen = { schluessel, liste: l, geholt: jetzt() };
      else fruehestens = jetzt() + NACH_FEHLER_MS;
      return zwischen?.liste ?? null;
    });
    return frage;
  }

  /**
   * Für den Takt: nach `HALTBAR_MS` wieder fragen — aber nur, wenn jemand hinsieht. Verdeckt bleibt der
   * Stand, wie er ist; beim Zurückkommen ruft die App den Takt sofort (visibilitychange → checkForUpdate).
   */
  const listeFuerTakt = (): Promise<NeuigkeitenListe | null> => {
    const alt = !!zwischen && jetzt() - zwischen.geholt >= HALTBAR_MS;
    return liste(alt && !d.zustand().sichtbar ? Number.POSITIVE_INFINITY : HALTBAR_MS);
  };

  /**
   * Für den Tipp: der Stand von jetzt, wenn er schnell kommt — sonst die Liste, die schon da ist. Kommt die
   * frische danach doch noch, zeichnet sie den Punkt neu (was im Fenster fehlte, bleibt damit ungesehen).
   */
  function listeFuerTipp(): Promise<NeuigkeitenListe | null> {
    const vorhanden = zwischen?.liste ?? null;
    const frisch = liste(TIPP_FRISCH_MS);
    // Nichts Altes zu zeigen, oder es wird gar nicht gefragt (frisch genug, Wartezeit nach Fehler): keine Frist nötig.
    if (!vorhanden || !frage) return frisch;
    return Promise.race([
      frisch,
      warte(TIPP_GEDULD_MS).then(() => {
        void frisch.then(zeichne);
        return vorhanden;
      }),
    ]);
  }

  /** Die neueste lesbare Version der Liste — der Server liefert sie zuerst, verlassen wird sich darauf nicht. */
  const neuesteVon = (l: NeuigkeitenListe): string =>
    l.eintraege.reduce(
      (a, e) => (istWebVersion(e.version) && (a === "" || istNeuerAls(e.version, a)) ? e.version : a),
      "",
    );

  /** Merken, bis wohin angesehen ist — nie rückwärts. */
  function merkeBis(neueste: string, vorher: string): void {
    if (neueste && (vorher === "" || istNeuerAls(neueste, vorher))) {
      gesehenSitzung = neueste;
      merker.schreibe(neueste);
      if (d.konto) void d.konto.setze(neueste);
    }
  }

  function zeigeFenster(l: NeuigkeitenListe, weg: "knopf" | "von-selbst", von: HTMLElement | null): void {
    const vorher = gesehen();
    const laeuft = d.laufendeVersion();
    const eintraege: FensterEintrag[] = l.eintraege.map((e) => ({
      ...e,
      neu: istUngesehen(e.version, vorher),
      nachNeuladen: istNeuerAls(e.version, laeuft),
    }));
    const neu = eintraege.filter((e) => e.neu).length;
    // Die neueste Version der Liste — der Server liefert sie zuerst, verlassen wird sich darauf nicht.
    // Nur lesbare zählen: gemerkt wird, was sich später auch vergleichen lässt.
    const neueste = neuesteVon(l);
    const update = d.updateBereit();
    const ort = ortVon(von);
    offen = true;
    try {
      // Das Menü am Telefon liegt über jedem Fenster — erst zu, dann zeigen.
      d.menueZu?.();
      zeige({
        eintraege,
        mehr: l.mehr && neu === eintraege.length,
        updateBereit: update,
        // Nach einem Tipp zurück zum Knopf (oder zu seinem Ersatz); von selbst geöffnet dorthin, wo der Fokus war.
        fokusZurueck: von ? [von, d.fokusErsatz?.()] : [],
        onNeuLaden: () => d.neuLaden(),
        onGelesen: () => {
          merkeBis(neueste, gesehen());
          zeichne();
          d.melde?.(`[was-ist-neu] gelesen weg=fenster bis=${kurzVersion(neueste)}`);
        },
        onZu: () => {
          offen = false;
          d.anzeige?.offen(false);
        },
      });
    } catch (err) {
      offen = false;
      throw err;
    }
    d.anzeige?.offen(true);
    // Beim Öffnen merken, nicht beim Schließen: ein Neuladen mittendrin darf den Punkt nicht zurückbringen.
    merkeBis(neueste, vorher);
    zeichne();
    d.melde?.(
      `[was-ist-neu] geöffnet weg=${weg} ort=${ort} neu=${neu} gezeigt=${eintraege.length} bis=${kurzVersion(neueste)} update=${update ? "ja" : "nein"}`,
    );
  }

  const warne = (err: unknown): void => console.warn("[was-ist-neu]", err instanceof Error ? err.message : err);

  async function versuche(): Promise<void> {
    try {
      const l = await listeFuerTakt();
      const s = zeichne();
      // Der Knopf ist der Weg — von selbst öffnet sich nichts.
      if (!l || l.knopf) return;
      // Rückweg (WAS_IST_NEU_KNOPF=0): wie Ticket 2925 — wenn die Pille da ist, etwas neu ist und nichts stört.
      if (offen || s.neu === 0 || !d.updateBereit()) return;
      if (fensterHindernis(d.zustand(), d.doc ?? document)) return;
      zeigeFenster(l, "von-selbst", null);
    } catch (err) {
      warne(err);
    }
  }

  async function oeffne(von?: HTMLElement | null): Promise<void> {
    if (offen) return;
    try {
      const l = await listeFuerTipp();
      // Während der Frage kann ein zweiter Tipp gekommen sein.
      if (offen || !l || l.eintraege.length === 0) return;
      zeigeFenster(l, "knopf", von ?? null);
    } catch (err) {
      warne(err);
    }
  }

  /** Läuft gerade ein Tipp auf den Chip (auch während des Ladens)? Ein zweiter Tipp legt keine zweite Karte. */
  let karteLaeuft = false;

  /**
   * Neu-Chip: die Karte in den Chat; war sie lange genug im Bild, merkt der Ablauf und zeichnet den Chip grau.
   * Mit Kurzfassung (Dienst bietet sie an, es gibt Ungesehenes): die Karte steht SOFORT da, mit dem Rückfall als Kurzpunkten
   * (erste Zeilen, gekürzt); die KI-Fassung kommt nach und tauscht die Punkte aus (lib/neu-chip.ts `kurzNachreichen`).
   */
  async function zeigeKarte(): Promise<void> {
    if (karteLaeuft) return;
    karteLaeuft = true;
    try {
      const l = await listeFuerTipp();
      if (!l || l.eintraege.length === 0 || !d.karte) return;
      // Vor dem Zählen kurz auf den Konto-Stand warten (ein anderes Gerät hat vielleicht schon gelesen) — nicht länger als ein Tipp warten darf.
      await Promise.race([abgleichen(0), warte(TIPP_GEDULD_MS)]);
      const vorher = gesehen();
      const eintraege = l.eintraege.map((e) => ({ ...e, neu: istUngesehen(e.version, vorher) }));
      const neu = eintraege.filter((e) => e.neu).length;
      const neueste = neuesteVon(l);
      // Kurzfassung: sofort der Rückfall, die KI kommt nach (mit dem Gelesen-Stand als Grenze).
      const rueck = l.kurz === true && neu > 0 ? rueckfallKurz(eintraege.filter((e) => e.neu).map((e) => e.text)) : [];
      const kurz = rueck.length > 0 ? rueck : null;
      const nachreichen = kurz ? holeKurzfassung(istWebVersion(vorher) ? vorher : VON_ANFANG, d.holen, KURZ_WARTE_MS) : null;
      d.karte({
        eintraege,
        mehr: l.mehr,
        ...(kurz ? { kurz } : {}),
        ...(nachreichen ? { kurzNachreichen: nachreichen } : {}),
        onGelesen: () => {
          merkeBis(neueste, gesehen());
          zeichne();
          d.melde?.(`[was-ist-neu] gelesen weg=karte bis=${kurzVersion(neueste)}`);
        },
        // Ansehen reicht (Martin 17.10.2026): war die Karte ein paar Sekunden im Bild, gilt alles darin als gelesen.
        onGesehen: () => {
          merkeBis(neueste, gesehen());
          zeichne();
          d.melde?.(`[was-ist-neu] gesehen weg=karte-sicht bis=${kurzVersion(neueste)}`);
        },
      });
      d.melde?.(`[was-ist-neu] karte gezeigt neu=${neu} gezeigt=${eintraege.length} kurz=${kurz ? kurz.length : 0}`);
    } catch (err) {
      warne(err);
    } finally {
      karteLaeuft = false;
    }
  }

  return {
    versuche,
    oeffne,
    zeigeKarte,
    gleiche: (sofort = false) => abgleichen(sofort ? KONTO_ZUSAMMEN_MS : KONTO_TAKT_MS - 5_000),
    istOffen: () => offen,
    stand: () => letzter,
  };
}
